import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchOrders, type Order, type OrderStatus } from '../api/orders';
import { OrderListCard } from '../components/OrderListCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';

const FINISHED: OrderStatus[] = ['delivered', 'cancelled', 'failed'];

const loadHistory = async (): Promise<Order[]> => {
  const pages = await Promise.all(FINISHED.map((status) => fetchOrders({ status, limit: 50, offset: 0 })));
  const rows = pages.flatMap((page) => page.data);
  return rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const HistoryPage = () => {
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({ queryKey: ['orders', 'history'], queryFn: loadHistory });
  const rows = data ?? [];
  const delivered = rows.filter((order) => order.status === 'delivered');
  const total = delivered.reduce((sum, order) => sum + Number(order.price || 0), 0);
  const currency = delivered[0]?.currency ?? 'EUR';
  const deliverer = user?.role === 'deliverer';

  return (
    <div className="animate-fade-in space-y-6">
      <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-6 shadow-soft">
        <p className="text-sm font-medium text-amber-800">Déjà fait</p>
        <PageHeader
          title="Historique"
          description={
            deliverer
              ? 'Les courses que vous avez terminées, refusées ou qui n’ont pas abouti. Le montant indiqué est celui de la course, entièrement pour vous.'
              : 'Les courses terminées, annulées ou qui n’ont pas abouti. Les courses encore en route restent dans Mes courses.'
          }
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terminées</p>
          <p className="mt-1 text-3xl font-semibold text-slate-900">{delivered.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {deliverer ? 'Courses encaissées' : 'Montant des courses livrées'}
          </p>
          <p className="mt-1 text-3xl font-semibold text-slate-900">
            {total.toFixed(2)} <span className="text-base font-medium text-slate-500">{currency}</span>
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Annulées ou échouées</p>
          <p className="mt-1 text-3xl font-semibold text-slate-900">{rows.length - delivered.length}</p>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error instanceof ApiError ? error.message : 'Historique indisponible'}
        </div>
      )}
      {!isLoading && rows.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-lg font-semibold text-slate-900">Rien de terminé pour le moment</p>
          <p className="mt-2 text-sm text-slate-500">Quand une course sera livrée ou annulée, elle apparaîtra ici.</p>
          <Link to="/orders" className="mt-4 inline-flex btn-primary">
            Voir les courses en cours
          </Link>
        </div>
      )}
      <div className="grid gap-4">
        {rows.map((order) => (
          <OrderListCard key={order.id} order={order} />
        ))}
      </div>
    </div>
  );
};
